<script setup>
import { format } from "date-fns";
import { applicationDocuments } from "~/constants";
import ProfileAvatar from "./ProfileAvatar.vue";

const { applicationId } = defineProps({
  applicationId: String,
});
const emit = defineEmits(["closeModal"]);

const toast = useToast();

const customDocuments = ref([]);
const isUniLoading = ref(false);

const { data: application } = await useApiFetch(
  `student-applications/${applicationId}`,
  {
    key: "student-application-detail",
    transform(application) {
      return application?.data;
    },
    onResponse({ response }) {
      // Pre-set custom application document
      const allDocs = response?._data?.data?.studentApplicationDocuments;

      if (allDocs?.length > 0) {
        let customDoc = [];

        allDocs.forEach((doc) => {
          if (
            ![
              ...applicationDocuments,
              "receipt",
              "acceptance_letter",
              "invoice",
            ].includes(doc.label)
          ) {
            customDoc.push(doc);
          }
        });
        customDocuments.value = customDoc.sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at),
        );
      }
    },
  },
);

const student = computed(() => {
  return {
    ...application.value?.student.profile,
    ...application.value?.student,
    profile_id: application.value?.student?.profile?.id,
  };
});

const university = computed(() => {
  return application.value?.university || {};
});

const program = computed(() => {
  return application.value?.program || {};
});

const studentAgent = computed(() => {
  return application.value?.agent || {};
});

const getDocument = (key) => {
  const doc = application.value.studentApplicationDocuments?.find(
    (doc) => doc.label === key,
  );
  return doc || null;
};

const isShowDocument = computed(() => {
  return application.value.status !== "booked";
});

const menu = ref();

function getAttributeData(key) {
  const attribute = application?.value?.program?.attributes?.data?.find(
    (d) => d.key === key,
  );
  return attribute ? attribute.value : "";
}

const addNewDocument = (inputType) => {
  customDocuments.value.push({
    label: "Empty",
    input_type: inputType,
    input_value: inputType === "text" ? "" : null,
  });
};

const onSaveAcademicData = async (data) => {
  const payload = {
    university_name: data.universityName,
    program_name: data.programName,
    field_of_study: data.fieldOfStudy,
    uni_start_date: data.startDate,
    uni_end_date: data.endDate,
  };
  await useApiFetch("profile/update/academic", {
    method: "POST",
    body: payload,
    onResponse() {
      toast.add({
        severity: "success",
        summary: "Academic info was saved.",
        life: 3000,
      });
      refreshNuxtData("student-application-detail");
    },
  });
};

/** Main App Flow */
const activeStage = ref("submit");
const applicationStages = ref([
  { label: "Received document", value: "review" },
  { label: "Verification", value: "verification" },
  { label: "Application in progress", value: "in-progress" },
  { label: "View Offer and make payment", value: "offer-payment" },
  { label: "Payment Review", value: "payment-review" },
  { label: "Enrollment", value: "enroll" },
]);

const finishedApplicationStages = computed(() => {
  const stages = [{ label: "Consulted and Submitted", value: "submit" }];
  const currentStatus = application?.value?.status;

  /** db -> dropout */
  if (currentStatus === "dropout") {
    stages.push({ label: "Received document", value: "review" });
    stages.push({ label: "Verification", value: "verification" });
    stages.push({ label: "Application in progress", value: "in-progress" });
    stages.push({ label: "Withdrew", value: "withdrew" });
    return stages;
  }

  /** db -> university_rejected */
  if (currentStatus === "university_rejected") {
    stages.push({ label: "Received document", value: "review" });
    stages.push({ label: "Verification", value: "verification" });
    stages.push({ label: "Application in progress", value: "in-progress" });
    stages.push({ label: "Offer Rejected", value: "offer-rejected" });
    return stages;
  }

  /** happy path */
  applicationStages.value.some((stage, index) => {
    if (studentApplicationStatus[index]?.value.includes(currentStatus)) {
      stages.push(stage);
      return true;
    }
    stages.push(stage);
    return false;
  });

  if (
    ["payment_success", "enroll"].includes(currentStatus) &&
    !stages.some((s) => s.value === "enroll")
  ) {
    stages.push({ label: "Enrollment", value: "enroll" });
  }

  return stages;
});

const editDisable = computed(() => {
  return !["booked", "consulted"].includes(application.value?.status);
});

// Watch for changes & set activeStage
watch(
  finishedApplicationStages,
  (newStages) => {
    if (newStages.length > 0) {
      activeStage.value = newStages[newStages.length - 1].value;
    }
  },
  { immediate: true },
);

watch(
  finishedApplicationStages,
  (newStages) => {
    if (newStages.length > 0) {
      activeStage.value = newStages[newStages.length - 1].value;
    }
  },
  { immediate: true },
);

const canDropoutApplication = computed(() => {
  return ![
    "booked",
    "payment_success",
    "dropout",
    "university_rejected",
  ].includes(application.value?.status);
});

const canDeleteApplication = computed(() => {
  return ["booked"].includes(application.value?.status);
});

const handleBreadcrumbClick = (stage) => {
  if (application?.value?.status !== "dropout") {
    activeStage.value = stage;
  }
};

const handleUniUpdate = (universityId) => {
  if (universityId) {
    isUniLoading.value = true;
    handleApplicationUpdate({ university_id: universityId });
    setTimeout(() => {
      isUniLoading.value = false;
    }, 300);
  }
};

const handleDegreeUpdate = (degree) => {
  if (degree)
    handleApplicationUpdate({
      degree,
    });
};

const handleProgramUpdate = (programId) => {
  isUniLoading.value = true;
  if (programId) handleApplicationUpdate({ program_id: programId });
};

const handleIntakeUpdate = (intake) => {
  if (intake)
    handleApplicationUpdate({
      intake_date: intake,
    });
};

const handleApplicationUpdate = async (payload) => {
  await useApiFetch(`student-applications/${applicationId}`, {
    method: "PUT",
    body: payload,
    onResponse({ response }) {
      console.log("success", response);
      toast.add({
        severity: "success",
        summary: "Application is updated.",
        life: 3000,
      });
      refreshNuxtData("student-application-detail");
      refreshNuxtData("my-applications");
      setTimeout(() => {
        isUniLoading.value = false;
      }, 300);
    },
  });
};

const breadcrumbContainer = ref(null);
const breadcrumbItems = ref({});

// mobile auto-scroll
const scrollToActiveStage = () => {
  nextTick(() => {
    if (breadcrumbContainer.value && breadcrumbItems.value[activeStage.value]) {
      breadcrumbItems.value[activeStage.value].scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }
  });
};

const getNearestStartDate = (intakeDates) => {
  return (
    intakeDates
      ?.map((intake) => {
        const startDate = new Date(intake.start_date);
        const now = new Date();
        // Set year to current year to only compare month and day
        startDate.setFullYear(now.getFullYear());
        // If date has passed this year, check next year
        if (startDate < now) {
          startDate.setFullYear(now.getFullYear() + 1);
        }
        return startDate;
      })
      ?.sort((a, b) => a - b)
      ?.find((date) => date > new Date()) || new Date()
  );
};

onMounted(scrollToActiveStage);
watch(activeStage, scrollToActiveStage);
</script>

<template>
  <div class="flex flex-col w-full relative">
    <!-- Header -->
    <div class="px-2.5 py-2 flex items-center justify-between sm:px-8 sm:py-5">
      <!-- mobile -->
      <StatusIcon
        :application="application"
        class="sm:hidden"
        class-name="text-wrap w-20"
      />
      <ProfileAvatar
        :imageSrc="student?.profile?.media?.at(0)?.original_url"
        :name="student?.name"
        class="sm:hidden"
        size="sm"
      />

      <!-- desktop -->
      <!-- Application info bar -->
      <div class="hidden items-center gap-4 sm:flex">
        <StatusIcon :application="application" />
        <div class="w-[1px] h-9 bg-gray-200" />
        <ProfileAvatar
          :imageSrc="student?.profile?.media?.at(0)?.original_url"
          :name="student?.name"
        />
        <div class="w-[1px] h-9 bg-gray-200" v-if="studentAgent?.id" />
        <div class="space-y-1" v-if="studentAgent?.id">
          <p class="text-xs font-medium text-color">Student Mentor</p>
          <ProfileAvatar
            :imageSrc="studentAgent?.profile?.media?.at(0)?.original_url"
            :name="studentAgent?.name"
            size="sm"
          />
        </div>
        <div class="w-[1px] h-9 bg-gray-200" />
        <div class="space-y-1">
          <p class="text-xs font-medium text-color px-2">University</p>
          <UniversitySelector
            :isConsultable="true"
            className="border-0 hover:bg-slate-100 max-w-52 truncate "
            :value="university?.id"
            @onChange="handleUniUpdate"
            :isDisabled="editDisable"
          />
        </div>
        <div class="w-[1px] h-9 bg-gray-200" />
        <div class="space-y-1">
          <p class="text-xs font-medium text-color">Degree</p>
          <EditableDegree
            @onChange="handleDegreeUpdate"
            :degree="application?.degree"
            :isDisabled="editDisable"
          />
        </div>

        <div class="w-[1px] h-9 bg-gray-200" />
        <div class="space-y-1">
          <p class="text-xs font-medium text-color px-2">Program</p>
          <ProgramSelector
            className="border-0 hover:bg-slate-100 max-w-52 truncate "
            :value="program?.id"
            :universityId="university?.id"
            @onChange="handleProgramUpdate"
            :isDisabled="editDisable"
          />
        </div>

        <div class="w-[1px] h-9 bg-gray-200" v-if="application?.priority" />

        <div class="space-y-1" v-if="application?.priority">
          <p class="text-xs font-medium text-color">Priority</p>

          <SolidBadge
            :severity="getPrioritySeverity(application?.priority)"
            size="sm"
            >{{ application?.priority }}</SolidBadge
          >
        </div>
        <div class="w-[1px] h-9 bg-gray-200" v-if="application?.intake_date" />
        <div class="space-y-2" v-if="application?.intake_date">
          <p class="text-xs font-medium text-color">Intake</p>
          <EditableIntake
            :intake="application?.intake_date"
            @onChange="handleIntakeUpdate"
            :isDisabled="editDisable"
          />
        </div>
      </div>

      <div class="flex items-center">
        <WithdrawApplication
          v-if="canDropoutApplication || canDeleteApplication"
          :applicationId="application?.id"
          @close="emit('closeModal')"
          :canDropout="canDropoutApplication"
          :canDelete="canDeleteApplication"
        />

        <Button
          rounded
          text
          icon="pi pi-times"
          @click="emit('closeModal')"
        ></Button>
      </div>
    </div>

    <!-- mobile -->
    <section class="block p-2 sm:hidden">
      <div class="p-4 bg-[#FAFAFA] rounded-md">
        <div class="flex items-start gap-x-8 mb-4">
          <div class="w-2/3">
            <p class="text-xs font-medium text-gray-700 mb-2">University</p>
            <UniversitySelector
              :isConsultable="true"
              className="border-0 hover:bg-slate-100 max-w-52 truncate "
              :value="university?.id"
              @onChange="handleUniUpdate"
              :isDisabled="editDisable"
            />
          </div>
          <div class="w-1/3">
            <p class="text-xs font-medium text-gray-700 mb-2">Degree</p>
            <EditableDegree
              @onChange="handleDegreeUpdate"
              :degree="application?.degree"
              :isDisabled="editDisable"
            />
          </div>
        </div>

        <div class="flex items-start gap-x-8">
          <div class="w-2/3">
            <p class="text-xs font-medium text-gray-700 mb-2">Program</p>
            <ProgramSelector
              className="border-0 hover:bg-slate-100 max-w-52 truncate "
              :value="program?.id"
              :universityId="university?.id"
              @onChange="handleProgramUpdate"
              :isDisabled="editDisable"
            />
          </div>
          <div class="w-1/3">
            <div class="space-y-1" v-if="application?.priority">
              <p class="text-xs font-medium text-gray-700 mb-2">Priority</p>
              <SolidBadge
                :severity="getPrioritySeverity(application?.priority)"
                size="sm"
                >{{ application?.priority }}</SolidBadge
              >
            </div>
          </div>
        </div>
      </div>
    </section>

    <Divider type="solid" class="!py-0 !my-0 hidden sm:block" />

    <!-- Content -->
    <!-- Desktop -->
    <div class="flex-1 hidden overflow-y-scroll h-full min-h-[70vh] sm:block">
      <Breadcrumb
        v-if="!['booked', 'consulted'].includes(application?.status)"
        :model="finishedApplicationStages"
        :pt="{
          root: 'py-3 px-8 border-b border-slate-200',
          separator: 'px-0',
          item: 'px-0',
        }"
      >
        <template #item="{ item }">
          <div class="flex items-center gap-x-2">
            <span
              :class="{
                'pi pi-check-circle text-muted hover:text-slate-600': true,
                '!text-blue-500 font-semibold': activeStage === item.value,
              }"
            ></span>
            <p
              :class="{
                'cursor-pointer text-sm text-muted hover:text-slate-600':
                  application?.status !== 'dropout',
                '!text-blue-500 font-semibold': activeStage === item.value,
                'pointer-events-none text-sm text-gray-400':
                  application?.status === 'dropout',
              }"
              @click="handleBreadcrumbClick(item.value)"
            >
              {{ item.label }}
            </p>
          </div>
        </template>
        <template #separator>&nbsp;</template>
      </Breadcrumb>

      <div
        v-if="
          !['booked', 'consulted'].includes(application?.status) &&
          activeStage !== 'submit'
        "
        class="w-full min-h-[70vh] bg-surface-50 flex items-start pt-10 justify-center"
      >
        <ReceiveDocument
          v-if="activeStage == 'review'"
          :application="application"
        />
        <Verification
          v-else-if="activeStage == 'verification'"
          :application="application"
        />
        <ApplicationInProgress
          v-else-if="activeStage == 'in-progress'"
          :application="application"
        />
        <OfferRejected
          v-else-if="activeStage == 'offer-rejected'"
          :application="application"
        />
        <OfferAndPayment
          v-else-if="activeStage == 'offer-payment'"
          :application="application"
        />
        <PaymentReview
          v-else-if="activeStage == 'payment-review'"
          :application="application"
        />
        <Enrollment
          v-else-if="activeStage == 'enroll'"
          :application="application"
        />
        <ApplicationWithDraw
          v-else-if="activeStage == 'withdrew'"
          :application="application"
        />
      </div>

      <div v-else class="grid grid-cols-12 h-full min-h-[70vh] pt-5">
        <div class="w-full col-span-7 space-y-8">
          <div class="pl-10 pr-20 space-y-3" v-if="studentAgent?.id">
            <p class="text-sm font-bold text-surface-900">Mentor Info</p>
            <div class="text-gray-900 text-sm space-y-1">
              <ProfileAvatar
                :imageSrc="studentAgent?.profile?.media?.at(0)?.original_url"
                :name="studentAgent?.name"
                size="sm"
              />
              <p>email: {{ studentAgent?.email }}</p>
              <p>phone: {{ studentAgent?.profile?.phone_number }}</p>
            </div>
          </div>

          <div class="px-10 space-y-3">
            <p class="text-sm font-bold text-surface-900">Program details</p>

            <div class="grid grid-cols-12 w-full h-full gap-x-5 gap-y-5 py-2">
              <TextItem label="Program" icon="book">{{
                program?.name
              }}</TextItem>

              <TextItem label="Degree" icon="book">{{
                application?.degree
              }}</TextItem>

              <TextItem label="Tuition fees" icon="wallet">
                {{ getTuitionFee(program?.tuition_fee) }}
              </TextItem>

              <TextItem label="Language" icon="comment">{{
                program?.tuition?.price
              }}</TextItem>

              <TextItem label="Location" icon="map-marker">
                {{ application?.university?.university_address?.city }}
              </TextItem>

              <TextItem
                v-if="application?.program?.intakes?.data?.length > 0"
                label="Intake date"
                icon="calendar"
              >
                {{
                  format(
                    getNearestStartDate(application?.program?.intakes?.data),
                    "dd MMM",
                  )
                }}
              </TextItem>

              <TextItem label="Duration" icon="hourglass">
                {{ getAttributeData("duration") }}
              </TextItem>

              <TextItem label="Application ID" icon="hashtag">
                {{ application?.application_id }}
              </TextItem>
            </div>
          </div>

          <div class="px-10 gap-3 pb-4 flex flex-col">
            <p class="text-sm font-bold text-surface-900">
              Your academic background
            </p>

            <AcademicInfo :application="application" />
            <!-- <AcademicInfoView :userProfile="application?.student?.agent_profile" /> -->
          </div>

          <!-- <div v-if="application?.program" class="px-10 gap-3 flex flex-col">
            <SuggestedProgram
              :searchKey="[
                getAttributeData('study_area'),
                getAttributeData('degree'),
              ]"
              :program="program?.id"
            />
          </div> -->
        </div>

        <div class="py-8 px-10 col-span-5 bg-[#FAFAFA] h-full">
          <div v-if="isShowDocument" class="space-y-6">
            <div class="space-y-2">
              <p class="text-sm font-bold text-surface-900">Upload Documents</p>

              <p class="text-sm text-surface-900">
                Please upload JPG, JPEG, PNG, or PDF files. Maximum file size is
                2MB.
              </p>
            </div>
            <Message severity="warn" v-if="application?.revise_comment">{{
              application?.revise_comment
            }}</Message>

            <hr />

            <UploadDocument
              v-for="(documentKey, index) in applicationDocuments"
              :key="documentKey + '_' + getDocument(documentKey)?.id"
              :label="documentKey"
              :document="getDocument(documentKey)"
              :applicationId="applicationId"
              :isDisabled="application?.status !== 'consulted'"
              :canDelete="application?.status == 'consulted'"
            />

            <div
              v-for="(document, index) in customDocuments"
              :key="document.input_type + '_' + (document?.id || index)"
            >
              <UploadDocument
                v-if="document.input_type === 'file'"
                :label="document.label"
                :document="getDocument(document.label)"
                :applicationId="applicationId"
                :isDisabled="application?.status !== 'consulted'"
                :canDelete="application?.status == 'consulted'"
              />
              <div v-else>
                <EditableText
                  :applicationId="application?.id"
                  :label="document.label"
                  :value="document.input_value"
                  :document="getDocument(document.label)"
                  @onChange="(value) => (document.input_value = value)"
                  :isDisabled="application?.status !== 'consulted'"
                  :canDelete="application?.status == 'consulted'"
                />
              </div>
            </div>
            <AddNewDocument
              :application="application"
              :customDocuments="customDocuments"
            />
          </div>
          <div v-else>
            <NoDocument :isNotUpload="application?.status === 'booked'" />
          </div>
        </div>
      </div>
    </div>

    <!-- mobile breadcrumb -->
    <!-- auto scroll x-axis to last state -->
    <Breadcrumb
      ref="breadcrumbContainer"
      v-if="!['booked', 'consulted'].includes(application?.status)"
      :model="finishedApplicationStages"
      :pt="{
        root: 'flex overflow-x-auto whitespace-nowrap py-2 px-4 border-b border-slate-200 sm:hidden',
        separator: 'px-1',
        item: 'flex items-center gap-x-1',
      }"
    >
      <template #item="{ item }">
        <div
          :ref="(el) => (breadcrumbItems[item.value] = el)"
          class="flex items-center gap-x-1"
        >
          <span
            :class="{
              'pi pi-check-circle text-muted hover:text-slate-600': true,
              '!text-blue-500 font-semibold': activeStage === item.value,
            }"
          ></span>
          <p
            :class="{
              'cursor-pointer text-xs text-muted hover:text-slate-600':
                application?.status !== 'dropout',
              '!text-blue-500 font-semibold': activeStage === item.value,
              'pointer-events-none text-xs text-gray-400':
                application?.status === 'dropout',
            }"
            @click="handleBreadcrumbClick(item.value)"
          >
            {{ item.label }}
          </p>
        </div>
      </template>
      <template #separator>&nbsp;</template>
    </Breadcrumb>

    <div
      v-if="
        !['booked', 'consulted'].includes(application?.status) &&
        activeStage !== 'submit'
      "
      class="w-full min-h-[70vh] bg-surface-50 flex items-start pt-10 justify-center sm:hidden"
    >
      <ReceiveDocument
        v-if="activeStage == 'review'"
        :application="application"
      />
      <Verification
        v-else-if="activeStage == 'verification'"
        :application="application"
      />
      <ApplicationInProgress
        v-else-if="activeStage == 'in-progress'"
        :application="application"
      />
      <OfferRejected
        v-else-if="activeStage == 'offer-rejected'"
        :application="application"
      />
      <OfferAndPayment
        v-else-if="activeStage == 'offer-payment'"
        :application="application"
      />
      <PaymentReview
        v-else-if="activeStage == 'payment-review'"
        :application="application"
      />
      <Enrollment
        v-else-if="activeStage == 'enroll'"
        :application="application"
      />
      <ApplicationWithDraw
        v-else-if="activeStage == 'withdrew'"
        :application="application"
      />
    </div>

    <!-- Content -->
    <!-- Mobile -->
    <section v-else class="block p-4 sm:hidden">
      <Accordion value="0" class="space-y-6 text-sm">
        <AccordionPanel value="0">
          <AccordionHeader class="mb-2 font-semibold text-gray-800"
            >Document Requirement</AccordionHeader
          >
          <AccordionContent>
            <div class="py-2 px-4 col-span-5 bg-[#FAFAFA] h-full">
              <div v-if="isShowDocument">
                <div class="space-y-3 h-full">
                  <Message severity="warn" v-if="application?.revise_comment">{{
                    application?.revise_comment
                  }}</Message>

                  <UploadDocument
                    v-for="(documentKey, index) in applicationDocuments"
                    :key="documentKey + '_' + getDocument(documentKey)?.id"
                    :label="documentKey"
                    :document="getDocument(documentKey)"
                    :applicationId="applicationId"
                    :isDisabled="application?.status !== 'consulted'"
                    :canDelete="application?.status == 'consulted'"
                  />

                  <div
                    v-for="(document, index) in customDocuments"
                    :key="document.input_type + '_' + (document?.id || index)"
                  >
                    <UploadDocument
                      v-if="document.input_type === 'file'"
                      :label="document.label"
                      :document="getDocument(document.label)"
                      :applicationId="applicationId"
                      :isDisabled="application?.status !== 'consulted'"
                      :canDelete="application?.status == 'consulted'"
                    />
                    <div v-else>
                      <EditableText
                        :applicationId="application?.id"
                        :label="document.label"
                        :value="document.input_value"
                        :document="getDocument(document.label)"
                        @onChange="(value) => (document.input_value = value)"
                        :isDisabled="application?.status !== 'consulted'"
                        :canDelete="application?.status == 'consulted'"
                      />
                    </div>
                  </div>
                  <AddNewDocument
                    :application="application"
                    :customDocuments="customDocuments"
                  />
                </div>
              </div>
              <div v-else>
                <NoDocument :isNotUpload="application?.status === 'booked'" />
              </div>
            </div>
          </AccordionContent>
        </AccordionPanel>
        <AccordionPanel value="1">
          <AccordionHeader class="mb-2 font-semibold text-gray-800"
            >Program Details</AccordionHeader
          >
          <AccordionContent>
            <div class="space-y-3">
              <div class="grid grid-cols-12 w-full h-full gap-x-5 gap-y-5 py-2">
                <TextItem label="Program" icon="book">{{
                  program?.name
                }}</TextItem>

                <TextItem label="Tuition fees" icon="wallet">
                  {{ getTuitionFee(program?.tuition_fee) }}
                </TextItem>

                <TextItem label="Language" icon="comment">-</TextItem>

                <TextItem label="Location" icon="map-marker">
                  {{ application?.university?.university_address?.city }}
                </TextItem>

                <TextItem
                  v-if="application?.program?.intakes?.data?.length > 0"
                  label="Intake date"
                  icon="calendar"
                >
                  {{
                    format(
                      getNearestStartDate(application?.program?.intakes?.data),
                      "dd MMM",
                    )
                  }}
                </TextItem>

                <TextItem label="Duration" icon="hourglass">{{
                  getAttributeData("duration")
                }}</TextItem>
                <TextItem label="Application ID" icon="hashtag">
                  {{ application?.application_id }}
                </TextItem>
              </div>
            </div>
          </AccordionContent>
        </AccordionPanel>
        <AccordionPanel value="2" v-if="studentAgent?.id">
          <AccordionHeader class="mb-2 font-semibold text-gray-800"
            >Mentor Info</AccordionHeader
          >
          <AccordionContent>
            <div class="text-gray-900 text-sm space-y-1">
              <ProfileAvatar
                :imageSrc="studentAgent?.profile?.media?.at(0)?.original_url"
                :name="studentAgent?.name"
                size="sm"
              />
              <p>email: {{ studentAgent?.email }}</p>
              <p>phone: {{ studentAgent?.profile?.phone_number }}</p>
            </div>
          </AccordionContent>
        </AccordionPanel>
        <AccordionPanel value="3">
          <AccordionHeader class="mb-2 font-semibold text-gray-800"
            >Academic Background</AccordionHeader
          >
          <AccordionContent>
            <AcademicInfo :application="application" />
          </AccordionContent>
        </AccordionPanel>
      </Accordion>
    </section>
  </div>
</template>
